import { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Switch,
  Button,
  Chip,
  Box,
  IconButton,
  Collapse,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Alert,
  TextField,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  HourglassEmpty as HourglassIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import api from '../../services/api';
import ActionsList from './ActionsList';
import { workingPulseSx } from '../../theme';
import type { AutoSubscriberProgress } from '../../hooks/useAutoSubscriberWebSocket';
import ScheduleBar from '../common/ScheduleBar';
import { useWaitingCountdown } from '../../utils/schedule';

interface ConfigCardProps {
  config: {
    id: number;
    name: string;
    targetGroupsUrls: string[];
    maxSubscriptionsPerDay: number;
    todaySubscriptions: number;
    totalSubscriptions: number;
    isActive: boolean;
    lastRunAt?: string | null;
    filterByActivity: boolean;
    filterByLocation?: string;
    filterByAgeMin?: number;
    filterByAgeMax?: number;
    account: {
      firstName: string;
      lastName: string;
    };
  };
  onUpdate: () => void;
  onDelete: (id: number) => void;
  progress?: AutoSubscriberProgress | null;
}

const getSkipReasonText = (reason: string): string => {
  switch (reason) {
    case 'already_subscribed': return 'Уже подписаны';
    case 'daily_limit': return 'Лимит достигнут';
    case 'account_limit': return 'Лимит аккаунта';
    case 'error': return 'Ошибка';
    default: return reason;
  }
};

const getPhaseIcon = (phase: AutoSubscriberProgress['phase']) => {
  switch (phase) {
    case 'searching': return <SearchIcon fontSize="small" />;
    case 'processing': return <SettingsIcon fontSize="small" />;
    case 'waiting': return <HourglassIcon fontSize="small" />;
    case 'completed': return <CheckCircleIcon fontSize="small" />;
    case 'error': return <ErrorIcon fontSize="small" />;
  }
};

const getPhaseColor = (phase: AutoSubscriberProgress['phase']): string => {
  switch (phase) {
    case 'searching': return 'info.main';
    case 'processing': return 'warning.main';
    case 'waiting': return 'warning.dark';
    case 'completed': return 'success.main';
    case 'error': return 'error.main';
  }
};

export default function ConfigCard({ config, onUpdate, onDelete, progress }: ConfigCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [runningNow, setRunningNow] = useState(false);

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    targetGroups: '',
    maxSubscriptionsPerDay: 30,
    filterByActivity: false,
    filterByLocation: '',
    filterByAgeMin: '' as string | number,
    filterByAgeMax: '' as string | number,
  });
  const openEdit = () => {
    setEditForm({
      name: config.name,
      targetGroups: config.targetGroupsUrls.join('\n'),
      maxSubscriptionsPerDay: config.maxSubscriptionsPerDay,
      filterByActivity: config.filterByActivity,
      filterByLocation: config.filterByLocation || '',
      filterByAgeMin: config.filterByAgeMin ?? '',
      filterByAgeMax: config.filterByAgeMax ?? '',
    });
    setEditOpen(true);
  };
  const submitEdit = async () => {
    const groups = editForm.targetGroups
      .split('\n')
      .map((g) => g.trim())
      .filter((g) => g.length > 0);
    if (!editForm.name.trim() || groups.length === 0) {
      toast.error('Укажите название и хотя бы одну группу');
      return;
    }
    setEditSaving(true);
    try {
      await api.patch(`/auto-subscriber/${config.id}`, {
        name: editForm.name.trim(),
        targetGroupsUrls: groups,
        maxSubscriptionsPerDay: editForm.maxSubscriptionsPerDay,
        filterByActivity: editForm.filterByActivity,
        filterByLocation: editForm.filterByLocation.trim() || undefined,
        filterByAgeMin: editForm.filterByAgeMin === '' ? undefined : Number(editForm.filterByAgeMin),
        filterByAgeMax: editForm.filterByAgeMax === '' ? undefined : Number(editForm.filterByAgeMax),
      });
      toast.success('Правило обновлено');
      setEditOpen(false);
      onUpdate();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Не удалось сохранить');
    } finally {
      setEditSaving(false);
    }
  };

  const { secLeft: waitSecLeft, pct: waitProgressPct, totalSec: waitTotalSec } =
    useWaitingCountdown(progress);
  const isWorking =
    !!progress && progress.phase !== 'completed' && progress.phase !== 'error';

  const handleRunNow = async () => {
    setRunningNow(true);
    try {
      const res = await api.post(`/auto-subscriber/${config.id}/run`);
      const { queued } = res.data.data || {};
      if (queued) toast.success('Запущено — следите за прогрессом');
      else toast(res.data.message || 'Не запущено', { icon: '⚠️' });
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Не удалось запустить');
    } finally {
      setRunningNow(false);
    }
  };

  const handleToggle = async () => {
    setIsToggling(true);
    try {
      await api.post(`/auto-subscriber/${config.id}/toggle`);
      toast.success(config.isActive ? 'Правило выключено' : 'Правило включено');
      onUpdate();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Не удалось переключить правило');
    } finally {
      setIsToggling(false);
    }
  };

  // Delete confirm via MUI dialog (consistent with the other tools)
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/auto-subscriber/${config.id}`);
      toast.success('Правило удалено');
      onDelete(config.id);
      setDeleteOpen(false);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Не удалось удалить');
    } finally {
      setDeleting(false);
    }
  };

  const dailyProgress = (config.todaySubscriptions / config.maxSubscriptionsPerDay) * 100;

  // Build filters description
  const filtersDesc = [];
  if (config.filterByActivity) filtersDesc.push('Активные');
  if (config.filterByLocation) filtersDesc.push(`Город: ${config.filterByLocation}`);
  if (config.filterByAgeMin || config.filterByAgeMax) {
    const ageRange = `${config.filterByAgeMin || '?'}-${config.filterByAgeMax || '?'}`;
    filtersDesc.push(`Возраст: ${ageRange}`);
  }

  return (
    <Card
      sx={{
        mb: 2,
        borderColor: isWorking ? 'info.main' : undefined,
        transition: 'border-color 0.3s, box-shadow 0.3s',
        ...(isWorking ? workingPulseSx : {}),
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography variant="h6" gutterBottom>
              {config.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Аккаунт: {config.account.firstName} {config.account.lastName}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">Целевые группы:</Typography>
              <Box sx={{ mt: 0.5 }}>
                {config.targetGroupsUrls.slice(0, 3).map((url, i) => {
                  const shortUrl = url.replace('https://vk.com/', '').replace('http://vk.com/', '');
                  return (
                    <Chip
                      key={i}
                      label={shortUrl.length > 20 ? shortUrl.substring(0, 20) + '...' : shortUrl}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  );
                })}
                {config.targetGroupsUrls.length > 3 && (
                  <Chip label={`+${config.targetGroupsUrls.length - 3}`} size="small" variant="outlined" />
                )}
              </Box>
            </Box>
            {filtersDesc.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">Filters:</Typography>
                <Box sx={{ mt: 0.5 }}>
                  {filtersDesc.map((f, i) => (
                    <Chip key={i} label={f} size="small" variant="outlined" color="info" sx={{ mr: 0.5, mb: 0.5 }} />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Box textAlign="right">
              <Typography variant="body2" color="text.secondary">
                Сегодня: {config.todaySubscriptions} / {config.maxSubscriptionsPerDay}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Всего: {config.totalSubscriptions}
              </Typography>
              <Box
                sx={{
                  width: 100,
                  height: 6,
                  bgcolor: 'grey.300',
                  borderRadius: 1,
                  mt: 0.5,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: `${Math.min(dailyProgress, 100)}%`,
                    height: '100%',
                    bgcolor: dailyProgress >= 100 ? 'error.main' : 'warning.main',
                    transition: 'width 0.3s',
                  }}
                />
              </Box>
            </Box>
            <Switch
              checked={config.isActive}
              onChange={handleToggle}
              disabled={isToggling}
              color="warning"
            />
          </Box>
        </Box>

        <ScheduleBar
          isActive={config.isActive}
          lastRunAt={config.lastRunAt}
          isWorking={isWorking}
          runningNow={runningNow}
          onRunNow={handleRunNow}
        />

        {/* Status Indicator */}
        {progress && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: 'action.hover',
              borderRadius: 1,
              border: 1,
              borderColor: getPhaseColor(progress.phase),
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={progress.details?.usersFound ? 1 : 0}>
              <Box sx={{ color: getPhaseColor(progress.phase) }}>
                {getPhaseIcon(progress.phase)}
              </Box>
              <Typography variant="body2" sx={{ color: getPhaseColor(progress.phase) }}>
                {waitSecLeft !== null
                  ? `Ожидание следующего действия: ${waitSecLeft} сек…`
                  : progress.message}
              </Typography>
            </Box>

            {/* Countdown (waiting phase) */}
            {waitSecLeft !== null && waitTotalSec > 0 && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={waitProgressPct}
                  color="warning"
                  sx={{ height: 6, borderRadius: 1 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {waitSecLeft} из {waitTotalSec} сек
                </Typography>
              </Box>
            )}

            {/* Progress bar for processing phase */}
            {progress.phase === 'processing' && progress.details?.usersFound && progress.details.usersFound > 0 && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={((progress.details.usersProcessed || 0) / progress.details.usersFound) * 100}
                  sx={{ height: 6, borderRadius: 1 }}
                  color="warning"
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {progress.details.usersProcessed || 0} / {progress.details.usersFound}
                </Typography>
              </Box>
            )}

            {/* Skipped reasons */}
            {progress.details?.skippedReasons && progress.details.skippedReasons.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {progress.details.skippedReasons.map((r, i) => (
                  <Chip
                    key={i}
                    label={`${getSkipReasonText(r.reason)}: ${r.count}`}
                    size="small"
                    variant="outlined"
                    color={r.reason === 'error' ? 'error' : 'default'}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Box>
          <Button
            size="small"
            startIcon={<ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} />}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Скрыть' : 'Показать'} историю
          </Button>
        </Box>
        <Box>
          <Button size="small" startIcon={<EditIcon />} onClick={openEdit} sx={{ mr: 1 }}>
            Изменить
          </Button>
          <IconButton color="error" size="small" onClick={() => setDeleteOpen(true)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      </CardActions>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent sx={{ pt: 0 }}>
          <ActionsList configId={config.id} />
        </CardContent>
      </Collapse>

      {/* ===== Edit dialog ===== */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Изменить правило</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Название правила"
            value={editForm.name}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Целевые группы (по одной ссылке на строку)"
            value={editForm.targetGroups}
            onChange={(e) => setEditForm({ ...editForm, targetGroups: e.target.value })}
            margin="normal"
            helperText="Из этих групп берутся участники для подписки"
          />
          <TextField
            fullWidth
            type="number"
            label="Макс. подписок в день"
            value={editForm.maxSubscriptionsPerDay}
            onChange={(e) =>
              setEditForm({ ...editForm, maxSubscriptionsPerDay: parseInt(e.target.value) || 1 })
            }
            margin="normal"
            inputProps={{ min: 1, max: 100 }}
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={editForm.filterByActivity}
                onChange={(e) => setEditForm({ ...editForm, filterByActivity: e.target.checked })}
              />
            }
            label="Только активные пользователи (заходили за 30 дней)"
          />
          <TextField
            fullWidth
            label="Фильтр по городу (пусто — без фильтра)"
            value={editForm.filterByLocation}
            onChange={(e) => setEditForm({ ...editForm, filterByLocation: e.target.value })}
            margin="normal"
            size="small"
          />
          <Box display="flex" gap={2} mt={1}>
            <TextField
              type="number"
              label="Возраст от"
              value={editForm.filterByAgeMin}
              onChange={(e) => setEditForm({ ...editForm, filterByAgeMin: e.target.value })}
              size="small"
              inputProps={{ min: 14, max: 100 }}
            />
            <TextField
              type="number"
              label="Возраст до"
              value={editForm.filterByAgeMax}
              onChange={(e) => setEditForm({ ...editForm, filterByAgeMax: e.target.value })}
              size="small"
              inputProps={{ min: 14, max: 100 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={editSaving}>
            Отмена
          </Button>
          <Button variant="contained" onClick={submitEdit} disabled={editSaving}>
            {editSaving ? 'Сохраняю…' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Delete confirm ===== */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Удалить правило?
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography>
              Будет удалено правило <b>«{config.name}»</b> и вся история его действий.
            </Typography>
            <Alert severity="warning">
              VK-аккаунт и накопленные счётчики аккаунта останутся — удаляется только это правило
              автоподписчика.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Отмена
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? 'Удаляю…' : 'Удалить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
